import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { ChatMessage, ReasoningEvent } from './types';

export interface ModelOption {
  id: string;
  label: string;
  sublabel: string;
}

interface ChatStore {
  messages: ChatMessage[];
  sessionId: string | null;
  connectionId: string | null;
  turnId: string | null;
  isProcessing: boolean;
  streamingContent: string;
  reasoningEvents: ReasoningEvent[];
  selectedAgentId: string;
  selectedModelId: string;
  useAgent: boolean;
  models: ModelOption[];
  systemPrompt: string;
  isConnected: boolean;
  connectionError: string | null;
  panelOpen: boolean;
  addMessage: (msg: ChatMessage) => void;
  setProcessing: (v: boolean) => void;
  setConnected: (v: boolean) => void;
  setConnectionError: (e: string | null) => void;
  setSessionId: (id: string | null) => void;
  setConnectionId: (id: string | null) => void;
  setTurnId: (id: string | null) => void;
  appendStreamingToken: (chunk: string) => void;
  finaliseStreaming: (turnId: string, timestamp: string) => void;
  addReasoningEvent: (event: ReasoningEvent) => void;
  clearReasoningEvents: () => void;
  setSelectedAgent: (agentId: string) => void;
  setSelectedModel: (modelId: string) => void;
  setUseAgent: (v: boolean) => void;
  setModels: (models: ModelOption[]) => void;
  setSystemPrompt: (prompt: string) => void;
  clearSession: () => void;
  setPanelOpen: (v: boolean) => void;
  togglePanel: () => void;
}

function makeId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export const useChatStore = create<ChatStore>()(
  persist(
    (set, get) => ({
      messages: [],
      sessionId: null,
      connectionId: null,
      turnId: null,
      isProcessing: false,
      streamingContent: '',
      reasoningEvents: [],
      selectedAgentId: 'yosef',
      selectedModelId: '',
      useAgent: true,
      models: [{ id: 'claude-sonnet-4-6', label: 'Sonnet 4.6', sublabel: 'Balanced' }],
      systemPrompt: '',
      panelOpen: false,
      isConnected: false,
      connectionError: null,

      addMessage: (msg) => set((state) => ({ messages: [...state.messages, msg] })),

      setProcessing: (v) => set({ isProcessing: v }),
      setSessionId: (id) => set({ sessionId: id }),
      setConnectionId: (id) => set({ connectionId: id }),
      setTurnId: (id) => set({ turnId: id }),

      appendStreamingToken: (chunk) =>
        set((state) => ({ streamingContent: state.streamingContent + chunk })),

      finaliseStreaming: (_turnId, timestamp) => {
        const { streamingContent } = get();
        if (streamingContent.length === 0) {
          set({ isProcessing: false, turnId: null, reasoningEvents: [] });
          return;
        }
        set((state) => ({
          messages: [
            ...state.messages,
            {
              id: makeId(),
              role: 'assistant' as const,
              content: state.streamingContent,
              timestamp,
              status: 'sent' as const,
            },
          ],
          streamingContent: '',
          isProcessing: false,
          turnId: null,
          reasoningEvents: [],
        }));
      },

      addReasoningEvent: (event) =>
        set((state) => ({ reasoningEvents: [...state.reasoningEvents, event] })),

      clearReasoningEvents: () => set({ reasoningEvents: [] }),

      setSelectedAgent: (agentId) =>
        set({
          selectedAgentId: agentId,
          messages: [],
          sessionId: null,
          streamingContent: '',
          turnId: null,
          reasoningEvents: [],
        }),

      setSelectedModel: (modelId) => set({ selectedModelId: modelId }),

      setUseAgent: (v) => set({ useAgent: v }),

      setModels: (models) => set({ models }),

      setSystemPrompt: (prompt) => set({ systemPrompt: prompt }),

      clearSession: () =>
        set({
          messages: [],
          sessionId: null,
          isProcessing: false,
          streamingContent: '',
          turnId: null,
          reasoningEvents: [],
        }),

      setPanelOpen: (v) => set({ panelOpen: v }),

      togglePanel: () => set((state) => ({ panelOpen: !state.panelOpen })),

      setConnected: (v) => set({ isConnected: v }),

      setConnectionError: (e) => set({ connectionError: e }),
    }),
    {
      name: 'chat-context',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        selectedAgentId: state.selectedAgentId,
        selectedModelId: state.selectedModelId,
        sessionId: state.sessionId,
      }),
    },
  ),
);
